import { useGroupsContext } from '@fs/zion-tree-groups'
import { usePermission } from '@fs/zion-permissions'
import { useFeatureFlag } from '@fs/zion-flags'

/**
 * The rule before the public rollout is layered on: a permitted user reaches user trees access either by having
 * opted in / owning a tree (canAccessCET), or by being a church member.
 * @param {boolean} isPermittedToAccessCET - Whether citizenship and country permit user trees
 * @param {boolean} canAccessCET - Whether the user has opted in to user trees features and researcherTrees flag is currently on
 * @param {boolean} isMember - Whether the user is a member of the church
 * @returns {boolean} True if user has access to CET
 */
function hasBaseCetAccess(isPermittedToAccessCET, canAccessCET, isMember) {
  return Boolean(isPermittedToAccessCET && (canAccessCET || isMember))
}

/**
 * Determines if a user has access to CET based on permission plus opt-in flag, church membership, or the public rollout flag.
 * @param {boolean} isPermittedToAccessCET - Whether citizenship and country permit user trees
 * @param {boolean} canAccessCET - Whether the user has opted in to user trees features and researcherTrees flag is currently on
 * @param {boolean} isMember - Whether the user is a member of the church
 * @param {boolean} userTreesPublicOn - Whether the public rollout flag is on
 * @returns {boolean} True if user has access to CET, whether by the base rule or the public rollout, false otherwise
 */
function hasCetAccess(isPermittedToAccessCET, canAccessCET, isMember, userTreesPublicOn) {
  // When public rollout flag is on, all permitted users get CET access
  if (userTreesPublicOn && isPermittedToAccessCET) {
    return true
  }
  return hasBaseCetAccess(isPermittedToAccessCET, canAccessCET, isMember)
}

/**
 * CET access without the public rollout, for pages where treeWeb_userTrees_public should not apply.
 * @returns {boolean} True if user has CET access disregarding the public rollout, false otherwise
 */
export function useCetBaseAccess() {
  const { isPermittedToAccessCET, canAccessCET } = useGroupsContext()
  const [isMember] = usePermission('FtMemberUiPermission')

  return hasBaseCetAccess(isPermittedToAccessCET, canAccessCET, isMember)
}

/**
 * Custom hook that determines if a user has creation rights to add a CET (Collaborative Edit Trees).
 * @returns {boolean} True if user has creation rights for CETs, false otherwise
 */
export default function useCetAccess() {
  const { isPermittedToAccessCET, canAccessCET } = useGroupsContext()
  const [isMember] = usePermission('FtMemberUiPermission')
  // ROLLBACK CAVEAT: Turning treeWeb_userTrees_public off does NOT revoke access for users who already visited.
  // usePersistCetAccess writes tree.cetTest=true permanently on first visit, which grants access even when this
  // flag is off. The rollout ratchets one way: can go up, but not back down without pulling treeWeb_researcherTrees,
  // which affects ALL opted-in users (members, labs, invites), not just those in the public rollout bucket.
  const userTreesPublicOn = useFeatureFlag('treeWeb_userTrees_public').isOn
  return hasCetAccess(isPermittedToAccessCET, canAccessCET, isMember, userTreesPublicOn)
}
